import { useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { FormikHelpers } from "formik";
import { useAppSelector } from "store/hooks";
import { userMetadataSelector } from "store/selectors";

import { AppEvents } from "services/events";
import { getPhoneNumberOrNull, showErrorModal } from "helpers";
import useAuth from "hooks/useAuth";

import { EModalTypes } from "uikit/Modal";

import {
  ExtendedUserProfileResponseDto,
  mutationAdminsControllerUpdateAdminProfile,
} from "utils/swagger_react_query";

import { AdminProfileFormType } from "./components";

export const useAdminProfilePage = () => {
  const translationPrefix = `my_profile`;

  const [isLoading, setLoading] = useState<boolean>(true);
  const currentUser = useAppSelector(userMetadataSelector);
  const { getCurrentUser } = useAuth();
  const [svoc, setSvoc] = useState<boolean>(false);
  const [canLeave, setCanLeave] = useState<boolean>(true);

  const getInitialValues = (
    userDetails?: Partial<ExtendedUserProfileResponseDto>,
  ): AdminProfileFormType => {
    return {
      firstName: userDetails?.firstName || "",
      lastName: userDetails?.lastName || "",
      jobTitle: userDetails?.jobTitle || "",
      phone: userDetails?.phone || "",
      email: userDetails?.email || "",
      avatarUrl: "",
      avatarFileName: "",
    };
  };

  const [initialValues, setInitialValues] = useState<AdminProfileFormType>(
    getInitialValues(currentUser?.activeCompanyUser || undefined),
  );

  useEffect(() => {
    fetchAdminInfo();
  }, []);

  const fetchAdminInfo = async () => {
    try {
      setLoading(true);
      const adminDetailsRes = await getCurrentUser();
      const user = adminDetailsRes;
      const formInitialValues: AdminProfileFormType = {
        firstName: user?.personalInfo?.firstName || "",
        lastName: user?.personalInfo?.lastName || "",
        jobTitle: user?.activeCompanyUser?.jobTitle || "",
        phone: user?.personalInfo?.phone || "",
        email: user?.personalInfo?.email || "",
        avatarUrl: "",
        avatarFileName: "",
      };
      setInitialValues(formInitialValues);
      return formInitialValues;
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (
    values: AdminProfileFormType,
    resetForm: FormikHelpers<AdminProfileFormType>["resetForm"],
  ) => {
    try {
      setLoading(true);
      const adminId = currentUser?.activeCompanyUser?.userId || "";

      const updatedAdminDetails = await mutationAdminsControllerUpdateAdminProfile({ adminId })({
        firstName: values.firstName,
        lastName: values.lastName,
        jobTitle: values?.jobTitle || null,
        phone: getPhoneNumberOrNull(values?.phone || ""),
        avatarUrl: null,
      });
      await getCurrentUser();

      AppEvents.emit("SetGlobalModal", {
        isOpen: true,
        type: EModalTypes.SUCCESS,
        title: <Trans i18nKey={`${translationPrefix}.profile_update_success_modal.title`} />,
        message: <Trans i18nKey={`${translationPrefix}.profile_update_success_modal.message`} />,
        mainButton: {
          text: <Trans i18nKey="buttons.ok" />,
          onClick: () => undefined,
        },
      });
      resetForm({ values: getInitialValues(updatedAdminDetails) });
      setCanLeave(true);
    } catch (error) {
      showErrorModal(error);
    } finally {
      setLoading(false);
    }
  };

  return {
    metadata: {
      translationPrefix,
      isLoading,
      initialValues,
      svoc,
      canLeave,
      setCanLeave,
    },
    actions: {
      handleSubmit,
    },
  };
};
